import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionModal, TextGrayDarkNormalRegular } from '@companio/companio_ui';
import AlertIcon from '@/assets/icons/alert_circle.svg?react';
import { DocumentTypes } from '@/types/document';
import {
  API_ERROR_CODE_DOCUMENT_PERIOD_CLOSED,
  API_ERROR_CODE_MERIT_DELETE_FAILED
} from '@/constants/documents';
import {
  DocumentDeletionBlockedDetail,
  DocumentDeletionBlockedReason
} from './DocumentDeletionBlockedModal.types';

export const DOCUMENT_DELETION_BLOCKED_EVENT = 'document-deletion-blocked';

// Mounted once in App, driven by a window event like AnnualReportLockModal, so every caller
// of the delete mutation gets the explanation.
export function DocumentDeletionBlockedModal() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [detail, setDetail] = useState<DocumentDeletionBlockedDetail | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      setDetail((event as CustomEvent<DocumentDeletionBlockedDetail>).detail || null);
      setIsOpen(true);
    };

    window.addEventListener(DOCUMENT_DELETION_BLOCKED_EVENT, handler);
    return () => window.removeEventListener(DOCUMENT_DELETION_BLOCKED_EVENT, handler);
  }, []);

  const close = () => {
    setIsOpen(false);
    setDetail(null);
  };

  const isPurchase = detail?.documentType === DocumentTypes.PURCHASE;

  function description() {
    if (detail?.errorCode === API_ERROR_CODE_DOCUMENT_PERIOD_CLOSED) {
      return t(
        isPurchase
          ? 'delete_invoice_blocked_period_closed_purchase_html'
          : 'delete_invoice_blocked_period_closed_sales_html'
      );
    }

    if (detail?.errorCode === API_ERROR_CODE_MERIT_DELETE_FAILED) {
      if (detail?.reason === DocumentDeletionBlockedReason.PAID) {
        return t('delete_invoice_blocked_accounting_paid_html');
      }

      if (detail?.reason === DocumentDeletionBlockedReason.RECONCILED) {
        return t('delete_invoice_blocked_accounting_reconciled_html');
      }

      if (detail?.retryable) {
        return t('delete_invoice_blocked_accounting_unavailable_html');
      }

      return t('delete_invoice_blocked_accounting_generic_html');
    }

    return detail?.message || t('default_api_error_message');
  }

  return (
    <ActionModal
      modalWidth="434px"
      icon={<AlertIcon />}
      isOpen={isOpen}
      clickOutside={close}
      title={t('delete_invoice_blocked_modal_title')}
      customDescription={
        <TextGrayDarkNormalRegular dangerouslySetInnerHTML={{ __html: description().toString() }} />
      }
      secondButton={{ onClick: close, text: t('ok') }}
    />
  );
}
